import { ChevronDown } from 'lucide-react'
import type React from 'react'
import { cn } from '@/utils/cn'

interface DetailSectionProps {
  icon: React.ReactNode
  label: string
  /** Omitted for a section that is one text (a recording's summary). */
  count?: number
  children: React.ReactNode
  /** Given with `onOpenChange`, the heading folds and opens the section; omitted, it stays open. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** The element id a shortcut scrolls to. */
  anchorId?: string
}

function Heading({ icon, label, count }: Pick<DetailSectionProps, 'icon' | 'label' | 'count'>) {
  return (
    <>
      {icon}
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--foreground)' }}>{label}</span>
      {count === undefined ? null : (
        <span
          style={{
            fontSize: 11,
            color: 'var(--muted-foreground)',
            fontFamily: 'var(--font-mono)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {count}
        </span>
      )}
    </>
  )
}

export function DetailSection({
  icon,
  label,
  count,
  children,
  open = true,
  onOpenChange,
  anchorId,
}: DetailSectionProps) {
  const headingStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: open ? 12 : 0,
    padding: '0 2px',
  }
  return (
    // A shortcut's scroll stops below the sticky row of shortcuts, not under it.
    <div id={anchorId} style={{ scrollMarginTop: 84 }}>
      {onOpenChange ? (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => onOpenChange(!open)}
          className="w-full rounded-md text-left hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          style={headingStyle}
        >
          <Heading icon={icon} label={label} count={count} />
          <ChevronDown
            aria-hidden
            className={cn(
              'ml-auto size-4 text-muted-foreground transition-transform',
              !open && '-rotate-90'
            )}
          />
        </button>
      ) : (
        <div style={headingStyle}>
          <Heading icon={icon} label={label} count={count} />
        </div>
      )}
      {open ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
      ) : null}
    </div>
  )
}
